<template>
	<FormCard class="">
		<h3>Process Waste EoL Breakdown - per {{ calculationOptions?.outputQuantity }} {{ selectedUnit?.unitName }} of {{ outputDescription }}</h3>
		<div class="flex">
			<ag-grid-vue
				:theme="resultGridTheme"
				style="width: 100%; min-height: 350px"
				:rowData="resultsByProcessWasteList"
				:columnDefs="agGridColumnDefs"
				:loadingOverlayComponentParams="{ loadingMessage: 'Loading result by step. Please wait.' }"
				:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No result by step found.' }"
				:overlayLoadingTemplate="'<span class=&quot;ag-overlay-loading-center&quot;>Loading result by step. Please wait.</span>'"
				:overlayNoRowsTemplate="'<span class=&quot;ag-overlay-no-rows-center&quot;>No result by step found.</span>'"
				:context="{}"
				:loadThemeGoogleFonts="true"
				:defaultColDef="{ wrapHeaderText: true, autoHeaderHeight: true }"
				:pinnedBottomRowData="pinnedBottomRow"
				@grid-ready="onGridReady"
			>
			</ag-grid-vue>
		</div>

		<!-- Dynamic Chart Component -->
		<!-- Chart Type Toggle -->
		<div class="flex mt-2 mb-2 justify-center">
			<div class="flex items-center space-x-4">
				<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
				<label class="relative inline-flex items-center cursor-pointer">
					<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
					<div
						class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
					></div>
				</label>
				<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
			</div>
		</div>
		<!-- Dynamic Charts Component with CSS Grid Layout -->
		<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
			<DynamicChartComponent
				v-for="emissionFactor in props.activeEmissionFactors"
				:key="emissionFactor.id"
				:title="
					props.calculationOptions?.useWeightedEmissionFactors
						? emissionFactor.emissionFactorName?.replace(/ \[.*\]$/, ' [mPt]')
						: emissionFactor.emissionFactorName
				"
				:emissionFactors="emissionFactor"
				:chartData="chartDataItems"
				:showPieChart="showPieChart"
				class="min-h-96 flex flex-col"
			/>
		</div>
		<!-- <div>
			<div>
				<vue-json-pretty :data="resultsByProcessWasteList" />
			</div>
		</div> -->
	</FormCard>
</template>
<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from "vue"
import {
	ApiClient,
	CalculationOptions,
	LifeCycleResultGraph,
	CustomProcessWasteEOL,
	EmissionFactors,
	Step,
	Unit,
} from "../../entities/NSwagGenerated"
import { useToast } from "primevue/usetoast"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faFloppyDisk, faXmarkLarge } from "@fortawesome/pro-regular-svg-icons"
import I2FormRow from "@/crispify/crispy-components/I2FormRow.vue"
import I2FormField from "@/crispify/crispy-components/I2FormField.vue"
import DynamicChartComponent from "./DynamicChartComponent.vue"
import type { ChartDataItem } from "./DynamicChartComponent.vue"

library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge)

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())

import { CellStyle, GridApi, GridReadyEvent, ValueGetterParams, ColDef } from "ag-grid-community"
const gridApi = ref<GridApi | null>(null)

// Computed property to map resultsByProcessWasteList to ChartDataItem format
const chartDataItems = computed((): ChartDataItem[] => {
	return resultsByProcessWasteList.value.map((item) => ({
		name: item.processWasteDetailName || "",
		emissionFactors: item.emissionFactors || {},
	}))
})

const pinnedBottomRow = computed(() => {
	if (!resultsByProcessWasteList.value.length) return []

	const totals = {
		processWasteDetailName: "Grand Total",
		climateChange: 0,
		waterConsumption: 0,
		emissionFactors: {} as { [key: string]: number },
	}

	for (const row of resultsByProcessWasteList.value) {
		totals.climateChange += row.climateChange ?? 0
		totals.waterConsumption += row.waterConsumption ?? 0
		if (row.emissionFactors) {
			for (const ef of props.activeEmissionFactors) {
				if (!ef.emissionFactorName) continue
				totals.emissionFactors[ef.emissionFactorName] =
					(totals.emissionFactors[ef.emissionFactorName] || 0) + (row.emissionFactors[ef.emissionFactorName] || 0)
			}
		}
	}

	return [totals]
})

function onGridReady(params: GridReadyEvent) {
	gridApi.value = params.api
}

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	activeEmissionFactors: {
		type: Array<EmissionFactors>,
		required: false,
		default: () => [],
	},
	calculationOptions: {
		type: CalculationOptions,
		required: false,
		default: undefined,
	},
	selectedStep: {
		type: Step,
		required: false,
		default: undefined,
	},
	selectedUnit: {
		type: Unit,
		required: false,
		default: undefined,
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
	steps: {
		type: Array as () => Step[],
		required: false,
		default: () => [],
	},
})

import { useStepOutputDescription } from "@/composables/stepOutputDescription"
const outputDescription = useStepOutputDescription(
	computed(() => props.tabName),
	computed(() => props.selectedStep),
	computed(() => props.steps as Step[])
)

import { AgGridVue } from "ag-grid-vue3"
import {
	getConditionalFormatColor,
	resultValueFormatter,
	valueToStringFormatter,
	resultGridTheme,
	BuildMinMaxValues,
	ColumnMinMax,
	BuildMinMaxValuesFromEmissionFactors,
} from "../composables/resultGridHelper"
import { generateChartColorsFromField } from "../composables/chartColorHelper"
const columnMinMax = ref<Array<ColumnMinMax>>([])

const agGridColumnDefs = computed(() => [
	{ field: "processWasteDetailName", headerName: "Waste Type" },
	...(props.calculationOptions?.useWeightedEmissionFactors
		? [
				{
					field: "totalWeightedScore",
					headerName: "Total Weighted Score [mPt]",
					valueGetter: (p: ValueGetterParams) => {
						return p.data?.totalWeightedScore ?? null
					},
					valueFormatter: (p: any) => valueToStringFormatter(p.value, false),
					aggFunc: "sum",
				},
			]
		: []),
	...props.activeEmissionFactors.map<ColDef>((ef) => ({
		field: ef.emissionFactorName,
		headerName: props.calculationOptions?.useWeightedEmissionFactors ? ef.emissionFactorName?.replace(/ \[.*\]$/, " [mPt]") : ef.emissionFactorName,
		valueGetter: (p) => {
			const value = ef.emissionFactorName == null || !p.data?.emissionFactors ? null : (p.data?.emissionFactors[ef.emissionFactorName] ?? null)
			return value
		},
		valueFormatter: (p) => valueToStringFormatter(p.value, false),
		cellStyle: (params): CellStyle | null => {
			// Don't apply conditional formatting to pinned rows
			if (params.node.rowPinned) {
				return { textAlign: "right", backgroundColor: "transparent" }
			}
			let minMax = columnMinMax.value.find((item) => item.columnName === ef.emissionFactorName)
			if (minMax) {
				return {
					backgroundColor: getConditionalFormatColor(params.value, minMax.min, minMax.max),
					textAlign: "right",
				}
			}
			return null
		},
		aggFunc: "sum",
	})),
])

function getResults(generalInfomationId: string) {
	barClimateChangeData.datasets[0].backgroundColor = []
	barWaterConsumptionData.datasets[0].backgroundColor = []
	barClimateChangeData.labels = []
	barClimateChangeData.datasets[0].data = []
	barWaterConsumptionData.labels = []
	barWaterConsumptionData.datasets[0].data = []

	getResultsByProcessWaste(generalInfomationId)
}

function impactCategoryChange(impactCategoryName: string) {
	displayLifeCycleResultClimateChangeGraph.value = false
	displayLifeCycleResultWaterConsumptionGraph.value = false

	barClimateChangeData.datasets[0].data = []
	barWaterConsumptionData.datasets[0].data = []

	barClimateChangeData.datasets[0].backgroundColor = []
	barWaterConsumptionData.datasets[0].backgroundColor = []
	barClimateChangeData.labels = []
	barWaterConsumptionData.labels = []

	if (impactCategoryName == "Climate change") {
		displayLifeCycleResultClimateChangeGraph.value = true
		resultsByProcessWasteList.value.forEach((item) => {
			barClimateChangeData.labels.push(item.processWasteDetailName || "")
			barClimateChangeData.datasets[0].data.push(item.climateChange || 0)
		})
		barClimateChangeData.datasets[0].backgroundColor = generateChartColorsFromField(resultsByProcessWasteList.value, "climateChange")
	} else if (impactCategoryName == "Freshwater use") {
		displayLifeCycleResultWaterConsumptionGraph.value = true
		resultsByProcessWasteList.value.forEach((item) => {
			barWaterConsumptionData.labels.push(item.processWasteDetailName || "")
			barWaterConsumptionData.datasets[0].data.push(item.waterConsumption || 0)
		})
		barWaterConsumptionData.datasets[0].backgroundColor = generateChartColorsFromField(resultsByProcessWasteList.value, "waterConsumption")
	}
}

let toast = useToast()
const displayResultsByProcessWaste = ref(false)
const displayLifeCycleResultClimateChangeGraph = ref(false)
const displayLifeCycleResultWaterConsumptionGraph = ref(false)
const selectedImpactCategory = ref("")

const resultsByProcessWasteList = ref([] as CustomProcessWasteEOL[])
const showPieChart = ref(true)
// Only Climate change and Freshwater use for process waste
const impactCategoryList = ["Climate change", "Freshwater use"]

const barClimateChangeData = {
	labels: [] as string[],
	datasets: [
		{
			label: "Climate Change [kg CO₂e]",
			backgroundColor: [] as string[],
			borderColor: "#1988E5",
			data: [] as number[],
		},
	],
}

const barWaterConsumptionData = {
	labels: [] as string[],
	datasets: [
		{
			label: "Freshwater use [m³]",
			backgroundColor: [] as string[],
			borderColor: "#1988E5",
			data: [] as number[],
		},
	],
}

const horizontalStackOptions = {
	indexAxis: "y",
	maintainAspectRatio: false,
	responsive: true,
	scales: {
		x: {
			beginAtZero: true,
		},
		y: {
			beginAtZero: true,
		},
	},
	plugins: {
		legend: {
			display: true,
			position: "top",
			labels: {
				boxWidth: 0,
				padding: 15,
			},
		},
	},
}

onMounted(() => {})

function getResultsByProcessWaste(generalInformationId: string) {
	if (generalInformationId != null) {
		apiClient.getProductProcessResultsByProcessWaste(generalInformationId, props.calculationOptions).then(
			(res) => {
				//console.log("getResultsByProcessWaste", res)
				if (res && res != null && res.length > 0) {
					res.forEach((item) => {
						if (item.processWasteDetailName == "Chemicals - without Water" || item.processWasteDetailName == "Water") {
							item.order = 1
						} else {
							;(item as any).order = 2
						}
					})

					resultsByProcessWasteList.value = res.sort((a, b) => a.order - b.order)
					displayResultsByProcessWaste.value = true
					var emissionFactors = resultsByProcessWasteList.value.map((item) => item.emissionFactors ?? {})
					columnMinMax.value = BuildMinMaxValuesFromEmissionFactors(emissionFactors)
					// Set dropdown to empty - user must manually select
					selectedImpactCategory.value = ""
				}
			},
			(err) => {
				//67441
				//toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				if (err.status == 400) {
					toast.add({ severity: "error", summary: `Error: ${err.response.message}`, life: 5000 })
					console.error("Error Id:", err.response.errorId)
				} else {
					toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				}
			}
		)
	}
}

const getGridApi = () => {
	return gridApi.value
}

defineExpose({
	getResults,
	getGridApi,
	getResultData: () => resultsByProcessWasteList.value,
})
</script>
<style></style>
